import { useSearch } from "@pineseed/gui/hook/useSearch";
import { useEffect } from "react";
import {
  Globe,
  MessageCircle,
  ExternalLink,
  BookOpen,
  PlayCircle,
  Loader2,
  AlertCircle,
} from "lucide-react";

/**
 * SearchDropdown Component
 * STRICT RULE: Pure UI only - no business logic, no external system calls
 * Renders search results in a dropdown with keyboard navigation
 */

interface SearchDropdownProps {
  className?: string;
}

export function SearchDropdown({ className = "" }: SearchDropdownProps) {
  const {
    searchState,
    navigateUp,
    navigateDown,
    executeCurrentSelection,
    executeSearchResult,
    stopSearch,
  } = useSearch();

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!searchState.isOpen) return;

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          navigateUp();
          break;
        case "ArrowDown":
          e.preventDefault();
          navigateDown();
          break;
        case "Enter":
          e.preventDefault();
          executeCurrentSelection();
          break;
        case "Escape":
          e.preventDefault();
          stopSearch();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    searchState.isOpen,
    navigateUp,
    navigateDown,
    executeCurrentSelection,
    stopSearch,
  ]);

  // Don't render if search is not open or no query
  if (!searchState.isOpen || !searchState.query.trim()) {
    return null;
  }

  // Get icon for result type
  const getResultIcon = (type: string, icon?: string) => {
    const iconProps = { size: 16, className: "text-muted-foreground" };

    if (icon) {
      switch (icon) {
        case "globe":
          return <Globe {...iconProps} />;
        case "message-circle":
          return <MessageCircle {...iconProps} />;
        case "external-link":
          return <ExternalLink {...iconProps} />;
        case "book-open":
          return <BookOpen {...iconProps} />;
        case "play-circle":
          return <PlayCircle {...iconProps} />;
      }
    }

    switch (type) {
      case "webpage":
        return <Globe {...iconProps} />;
      case "chat-message":
        return <MessageCircle {...iconProps} />;
      default:
        return <ExternalLink {...iconProps} />;
    }
  };

  // Get searcher icon
  const getSearcherIcon = (searcherType: string) => {
    const iconProps = { size: 14, className: "text-muted-foreground" };

    switch (searcherType) {
      case "web":
        return <Globe {...iconProps} />;
      case "chat":
        return <MessageCircle {...iconProps} />;
      default:
        return <ExternalLink {...iconProps} />;
    }
  };

  return (
    <div
      className={`absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-96 overflow-y-auto z-50 ${className}`}
    >
      {searchState.sections.length === 0 && !searchState.isSearching && (
        <div className="p-4 text-center text-muted-foreground">
          <AlertCircle size={24} className="mx-auto mb-2" />
          <p>No results found for "{searchState.query}"</p>
          <p className="text-xs mt-1">
            Try different keywords or check your spelling
          </p>
        </div>
      )}

      {searchState.sections.map((section, sectionIndex) => (
        <div
          key={section.searcherType}
          className="border-b border-border last:border-b-0"
        >
          {/* Section Header */}
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border-b border-border">
            {getSearcherIcon(section.searcherType)}
            <span className="text-sm font-medium text-foreground">
              {section.title}
            </span>
            {section.isLoading && (
              <Loader2
                size={12}
                className="animate-spin text-muted-foreground ml-auto"
              />
            )}
            {section.error && (
              <div title={section.error}>
                <AlertCircle size={12} className="text-destructive ml-auto" />
              </div>
            )}
          </div>

          {/* Results */}
          {section.results.length > 0 && (
            <div>
              {section.results.map((result, resultIndex) => {
                const isSelected =
                  searchState.selectedSectionIndex === sectionIndex &&
                  searchState.selectedResultIndex === resultIndex;

                return (
                  <button
                    key={result.id}
                    className={`w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors ${
                      isSelected
                        ? "bg-brand-500/10 border-l-2 border-brand-500"
                        : ""
                    }`}
                    onClick={() => executeSearchResult(result)}
                  >
                    <div className="flex items-start gap-3">
                      {getResultIcon(result.type, result.icon)}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {result.title}
                        </div>
                        {result.description && (
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {result.description}
                          </div>
                        )}
                        {result.url && (
                          <div className="text-xs text-brand-600 mt-1 truncate">
                            {result.url}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Empty state for section */}
          {section.results.length === 0 &&
            !section.isLoading &&
            !section.error && (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                No {section.title.toLowerCase()} results found
              </div>
            )}

          {/* Error state for section */}
          {section.error && (
            <div className="px-3 py-2 text-xs text-destructive">
              Error: {section.error}
            </div>
          )}
        </div>
      ))}

      {/* Footer with keyboard shortcuts */}
      {searchState.sections.some((s) => s.results.length > 0) && (
        <div className="px-3 py-2 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Use ↑↓ to navigate, Enter to select, Esc to close</span>
          </div>
        </div>
      )}
    </div>
  );
}
